/* eslint-disable no-magic-numbers */

import { add, format, isLeapYear, parse } from 'date-fns';
import { FinancialConfig } from '@futrli-sdk/predict';
import { forceLocalDate } from './dates';
import { inRange } from 'lodash';

/**
 * Returns true or false depending on if the financial year spans two calendar years
 * @param financialConfig - Financial config of the organisation
 */
export const doesFYSpanTwoCalendarYears = (financialConfig: FinancialConfig) => {
  const startYear = parseInt(financialConfig.current_year.start.substring(0, 4), 10);
  const endYear = parseInt(financialConfig.current_year.end.substring(0, 4), 10);
  return startYear !== endYear;
};

/**
 * Returns the financial year number that the given date belongs to
 * @param financialConfig - Financial config of the organisation
 * @param date - The date to get the financial year for
 */
export const getDateFY = (financialConfig: FinancialConfig, dateInput: Date | string) => {
  const date = typeof dateInput === 'string' ? forceLocalDate(dateInput) : dateInput;
  const fyDates = getFYStartEnd(financialConfig, date.getFullYear());
  const financialYearEnd = forceLocalDate(fyDates.end);
  return date <= financialYearEnd ? date.getFullYear() : date.getFullYear() + 1;
};

/**
 * Returns the financial year start and end info for the given date and financial config
 * @param financialConfig - Financial config of the organisation
 * @param inputDateStr - The date to get the financial year from (YYYY-MM-DD). Defaults to today.
 */
export const getDateFYStartEnd = (financialConfig: FinancialConfig, inputDateStr?: string | null) => {
  const inputDateFinancialYear = getDateFY(financialConfig, inputDateStr || '');
  return getFYStartEnd(financialConfig, inputDateFinancialYear);
};

/**
 * Returns a valid day / month number for the FY start date for the given end day / month values
 * @param dayInput - The year end day value
 * @param monthInput - The year end month value (NOT zero indexed, 1 = January)
 */
export const getDayMonthStartFromDayMonthEnd = (dayInput: number | string, monthInput: number | string) => {
  const endMonth = parseInt(String(monthInput), 10);
  const endDay = parseInt(String(dayInput), 10);

  let startDay: number;
  let startMonth: number;

  const isYearEndInLeapYear = endMonth === 2 && endDay === 29;
  const isCalendarFinancialYear = endMonth === 12 && endDay === 31;
  if (isYearEndInLeapYear) {
    // If ending on Feb 29th (leap year date), FY always starts on march 1st
    startDay = 1;
    startMonth = 3;
  } else if (isCalendarFinancialYear) {
    // If ending on Dec 31st, FY is calendar year and starts on 1st Jan
    startDay = 1;
    startMonth = 1;
  } else {
    const startDate = add(parse(`${endMonth}-${endDay}`, 'MM-dd', new Date()), { days: 1 });
    if (!isNaN(startDate.valueOf())) {
      startDay = startDate.getDate();
      startMonth = startDate.getMonth() + 1;
    } else {
      throw new Error('Invalid day / month value');
    }
  }

  return [startDay, startMonth] as const;
};

/**
 * Returns a valid FinancialConfig given a year end day and month value
 * @param dayInput - The year end day value
 * @param monthInput - The yesr end month value (NOT zero indexed - 1 = January)
 */
export const getFYConfigFromDayMonthEnd = (dayInput: number | string, monthInput: number | string): FinancialConfig => {
  const endMonth = parseInt(String(monthInput), 10);
  const endDay = parseInt(String(dayInput), 10);

  let endDate: Date;
  let endYear: number;
  let startDate: Date;
  let startYear: number;

  const today = forceLocalDate(format(new Date(), 'yyyy-MM-dd')); // Today at 00:00:00.

  const isCalendarFinancialYear = endMonth === 12 && endDay === 31;

  if (isNaN(endMonth) || isNaN(endDay)) {
    throw new Error('Invalid day / month value');
  }

  const [startDay, startMonth] = getDayMonthStartFromDayMonthEnd(endDay, endMonth);

  // Now we work out the current start and end year
  if (isCalendarFinancialYear) {
    startYear = endYear = today.getFullYear();
  } else {
    startDate = forceLocalDate(`${today.getFullYear()}-${startMonth}-${startDay}`);
    if (startDate > today) {
      startYear = today.getFullYear() - 1;
    } else {
      startYear = today.getFullYear();
    }
    endYear = startYear + 1;
  }

  startDate = forceLocalDate(`${startYear}-${startMonth}-${startDay}`);
  endDate = parse(`${endYear}-${endMonth}-${endDay}`, 'yyyy-MM-dd', new Date());

  if (isNaN(endDate.valueOf())) {
    endDate = parse(`${endYear}-${endMonth}-${endDay - 1}`, 'yyyy-MM-dd', new Date());
  }

  return {
    current_year: {
      start: format(startDate, 'yyyy-MM-dd'),
      end: format(endDate, 'yyyy-MM-dd')
    },
    year_end: {
      day: endDay,
      month: endMonth
    },
    reporting_lineref_version: 2,
    accounting_rules: 'default', // Might need to revisit this...
    allow_google_sheet_import: false,
    allow_reorganising_groups: false,
    unreconciled_amount: null,
    unreconciled_transactions_count: null,
    dimensions: [],
    dimensions_enabled: false
  };
};

/**
 * Returns the financial year start and end info for the given year and financial config
 * @param financialConfig - Financial config of the organisation
 * @param financialYear - The financial year to generate the dates for 2024 would be for 2023-2024 if
 *                        the financial year spans across two different calendar years.
 */
export const getFYStartEnd = (financialConfig: FinancialConfig, financialYearInput: number | string) => {
  const financialYear = typeof financialYearInput === 'string' ? parseInt(financialYearInput, 10) : financialYearInput;
  const calendarYearDiff = doesFYSpanTwoCalendarYears(financialConfig) ? 1 : 0;
  const startFinancialYear = financialYear - calendarYearDiff;

  const isFYEndOnFeb28th = financialConfig.year_end.month === 2 && financialConfig.year_end.day === 28;
  const isFYEndOnFeb29th = financialConfig.year_end.month === 2 && financialConfig.year_end.day === 29;
  const isTargetYearLeapYear = isLeapYear(forceLocalDate(`${financialYear}-01-01`));

  if (isFYEndOnFeb28th) {
    // If FYE is Feb 28th, on a leap year the FY start is technically Feb 29th.
    // Skip that and always use 1st March.
    return {
      start: `${startFinancialYear}-03-01`,
      end: `${financialYear}-02-28`
    };
  } else if (isFYEndOnFeb29th) {
    // A FYE of Feb 29th is only possible on a leap year, so on a non-leap year,
    // the FYE will be Feb 28th. If you are here because you have an org with a FYE
    // of 29th Feb, you're in for a bad day.
    return {
      start: `${startFinancialYear}-03-01`,
      end: `${financialYear}-02-${isTargetYearLeapYear ? '29' : '28'}`
    };
  } else {
    return {
      start: `${startFinancialYear}${financialConfig.current_year.start.substring(4)}`,
      end: `${financialYear}${financialConfig.current_year.end.substring(4)}`
    };
  }
};

/**
 * Will return true or false depending upon if the given date is within the given financial year range.
 * Range is inclusive of lower and upper limits, so values 2,3,4 would be true for a range of 2-4
 * @param financialConfig - Financial config of the organisation
 * @param inputDateStr - The date to test (YYYY-MM-DD)
 * @param lowerOffset - The lower offset, so 5 would mean upto 5 years previous
 * @param upperOffset - The upper offset, so 5 would mean upto 5 years in the future
 */
export const isDateFYWithinRange = (
  financialConfig: FinancialConfig,
  inputDateStr: string,
  lowerOffset: number,
  upperOffset: number
) => {
  const currentYear = getDateFY(financialConfig, financialConfig.current_year.start);
  const inputYear = getDateFY(financialConfig, inputDateStr);
  return inRange(inputYear, currentYear - lowerOffset, currentYear + upperOffset + 1);
};
